import type { CSSProperties } from 'react';

import { flyOffsetOf, flyTransform, OVERLAY_MOTION_MS, type OverlayMotion } from './motion';
import { OVERLAY_MARGIN, type AnchorRect, type FrozenPlacement } from './placement';
import { overlayZIndexOf, scopeRulesOf, type OverlayScope, type OverlayTier } from './scope';

/**
 * 样式。
 *
 * 这里用内联样式而不是 Tailwind 类名，原因是这个包**不带任何 CSS**：
 * 令牌（`styles/tokens.css`）是唯一视觉来源，Tailwind 的命名空间映射在应用侧。
 * 内联 `var(--*)` 让浮层的层级、遮挡关系在任何消费方里都成立 ——
 * 而层级恰恰是本模块的规格（"任何东西都不许遮住遥测"），不能依赖"应用有没有扫到这个文件"。
 *
 * 只用令牌，不写死色值。个别令牌目前还没在 tokens.css 里定义（阴影、蒙版底色），
 * 这里用 `var(--x, 兜底)` 的形式留出覆盖口：视觉方在 tokens.css 里补上即可生效，无需改代码。
 */

/**
 * 面板容器：定位锚点，也是层级所在。
 *
 * `depth` 叠在**基座**层级之上，所以同一档里按打开顺序叠高。
 * `tier` 只换基座：`confirm` 得到一个在任何作用域之上都成立的基座，
 * 于是"从功能性弹窗内部发起的确认"不会被它自己的蒙版压住（见 `tokens.css` 的 `--z-confirm`）。
 */
export function overlayLayerStyle(
  scope: OverlayScope,
  depth: number,
  tier?: OverlayTier | undefined,
): CSSProperties {
  const rules = scopeRulesOf(scope);
  return {
    position: 'fixed',
    inset: 0,
    // 同一档内按打开顺序叠高；跨档由令牌保证 blocking 在 realtime 之上、confirm 在两者之上
    zIndex: `calc(${overlayZIndexOf(scope, tier)} + ${Math.max(depth, 0)})`,
    // 这一行是 realtime 的核心机制：整层不接收指针事件，只有面板自己 `pointer-events: auto`。
    // 于是底下的实时数据"看得见也点得到"，而浮层内容照常可交互。
    // 换成蒙版（blocking）时这里变成 auto —— 蒙版必须吃掉事件，否则"中断操作"就是假的。
    pointerEvents: rules.scrim ? 'auto' : 'none',
  };
}

export function overlayScrimStyle(): CSSProperties {
  return {
    position: 'absolute',
    inset: 0,
    // `--scrim` 尚未在 tokens.css 定义：用主题自己的页面底色 + 透明度兜底，
    // 这样浅色/深色两套主题都不会出现"写死的黑"或"写死的白"。定义 --scrim 后自然接管。
    background: 'var(--scrim, var(--surface-0))',
    opacity: 'var(--scrim-opacity, 0.82)',
    // 模糊让"底下的东西此刻不可操作"这件事一眼可见，比单纯压暗更明确。
    // 强度走令牌：不同产品的"模糊多少"是视觉决策，不该埋在浮层代码里。
    backdropFilter: 'blur(var(--overlay-backdrop-blur, 4px))',
  };
}

export interface OverlayPanelStyleOptions {
  /**
   * 阴影策略。
   *
   * 按钮确认气泡**显式** `none`：它紧贴触发按钮，加阴影等于宣称"我浮在上面"，
   * 而它恰恰是"从按钮里长出来"的一部分；更要紧的是，靠"令牌没定义所以等于 none"
   * 是隐式的 —— 谁在 tokens.css 里补一个 `--overlay-shadow` 就会悄悄改变确认框的观感。
   * 功能性弹窗才用令牌（它确实浮在整个页面之上）。
   */
  readonly shadow: 'none' | 'token';
  /** 垂直对齐：`center` 用于功能性弹窗；`top` 沿用冻结位置 */
  readonly verticalAlign: 'top' | 'center';
  /** 视觉阶段。`undefined` = 不做动画（功能性弹窗要求即时） */
  readonly motion: OverlayMotion | undefined;
  /** 飞入/飞出的方向来源（冻结的锚点矩形，不是"现在的"锚点） */
  readonly flyOrigin: AnchorRect | null;
}

export function overlayPanelStyle(placement: FrozenPlacement, options: OverlayPanelStyleOptions): CSSProperties {
  const style: CSSProperties = {
    position: 'absolute',
    top: `${placement.top}px`,
    maxWidth: `${placement.maxWidth}px`,
    maxHeight: placement.maxHeight,
    overflowY: 'auto',
    // 父层是 pointer-events: none，面板必须显式收回来
    pointerEvents: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    background: 'var(--surface-1)',
    color: 'var(--fg)',
    border: '1px solid var(--line-strong)',
    // 圆角令牌在应用侧的 @theme inline 里（不会被输出成 CSS 变量），因此必须带兜底值
    borderRadius: 'var(--radius-card, 0.75rem)',
    boxShadow: options.shadow === 'none' ? 'none' : 'var(--overlay-shadow, none)',
  };

  /**
   * 居中的基础 transform。
   *
   * 它由视口与自身尺寸决定，与内容无关 —— 内容变宽只会改变百分比解析出的像素值，
   * 但"水平/垂直居中的意图"不变，所以不会出现"内容一变面板就跳"。
   */
  let baseTransform: string | undefined;
  if (placement.centered) {
    style.left = '50%';
    if (options.verticalAlign === 'center') {
      style.top = '50%';
      baseTransform = 'translate(-50%, -50%)';
      // 垂直居中后，冻结的 maxHeight（按 top 算的）就没有意义了：改成上下各留一条边距
      style.maxHeight = `calc(100vh - ${2 * OVERLAY_MARGIN}px)`;
    } else {
      baseTransform = 'translateX(-50%)';
    }
  } else {
    style.left = `${placement.left}px`;
  }

  if (options.motion === undefined) {
    // 无动画：直接落在最终位置。功能性弹窗走这条路（即时出现，不表演）
    if (baseTransform !== undefined) style.transform = baseTransform;
    return style;
  }

  /*
   * 有动画时：transition 只挂在 `transform` / `opacity` 上。
   * `top` / `left` 永远不进 transition —— 这就是"动画只影响视觉效果，不影响最终位置"的机制，
   * 而不是一条约定：位置属性在动画期间根本没变过。
   */
  style.transition = `transform ${OVERLAY_MOTION_MS}ms ease-out, opacity ${OVERLAY_MOTION_MS}ms ease-out`;

  if (options.motion === 'settled') {
    if (baseTransform !== undefined) style.transform = baseTransform;
    style.opacity = 1;
    return style;
  }

  // entering 与 exiting 共用同一个目标态：起点=终点 ⇒ 关闭时原路收回锚点。
  style.transform = flyTransform(baseTransform, flyOffsetOf(placement, options.flyOrigin));
  // 缩放绕着"锚点那一角"发生，才像从按钮里长出来；绕中心缩放看着像从面板中间炸开
  style.transformOrigin = '0 0';
  style.opacity = 0;
  if (options.motion === 'exiting') {
    // 收回动画期间面板还没卸载（卸载了就看不到"收回去"），但它已经不该再挡住任何点击
    style.pointerEvents = 'none';
  }
  return style;
}

export function overlayTitleStyle(): CSSProperties {
  return {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: 'var(--fg)',
  };
}
